import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from 'antd';
import { Button, FormField, Input } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import { authApi } from '../api/auth-api';
import {
  passwordResetRequestSchema,
  passwordResetConfirmSchema,
  type PasswordResetRequestValues,
  type PasswordResetConfirmValues,
} from '../model/schemas';
import { mapAuthError } from '../lib/map-auth-error';

type Step = 'request' | 'confirm' | 'done';

export function ForgotPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('request');
  const [identity, setIdentity] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const requestForm = useForm<PasswordResetRequestValues>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { identity: '' },
  });

  const confirmForm = useForm<PasswordResetConfirmValues>({
    resolver: zodResolver(passwordResetConfirmSchema),
    defaultValues: { identity: '', code: '', newPassword: '', confirmPassword: '' },
  });

  const requestMutation = useMutation({
    mutationFn: (values: PasswordResetRequestValues) =>
      authApi.requestPasswordReset(values.identity),
    onSuccess: (_data, values) => {
      setIdentity(values.identity);
      confirmForm.setValue('identity', values.identity);
      setStep('confirm');
    },
    onError: (error) => setSubmitError(mapAuthError(error)),
  });

  const confirmMutation = useMutation({
    mutationFn: (values: PasswordResetConfirmValues) =>
      authApi.confirmPasswordReset({
        identity: values.identity,
        code: values.code,
        newPassword: values.newPassword,
      }),
    onSuccess: () => setStep('done'),
    onError: (error) => {
      if (!applyApiFieldErrors(error, confirmForm.setError)) {
        setSubmitError(mapAuthError(error));
      }
    },
  });

  if (step === 'done') {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="text-sm">{t('auth.forgot.done')}</p>
        <Button onClick={() => navigate(ROUTES.login)}>{t('auth.forgot.goToSignIn')}</Button>
      </div>
    );
  }

  if (step === 'request') {
    return (
      <FormProvider {...requestForm}>
      <form
        className="flex flex-col gap-4"
        onSubmit={requestForm.handleSubmit((v) => {
          setSubmitError(null);
          requestMutation.mutate(v);
        })}
        noValidate
      >
        <p className="text-sm opacity-70">{t('auth.forgot.step1Hint')}</p>
        <FormField
          label={t('common.email')}
          required
          error={requestForm.formState.errors.identity?.message}
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              {...requestForm.register('identity')}
            />
          )}
        </FormField>

        {submitError ? <Alert type="error" showIcon message={submitError} /> : null}

        <Button type="submit" loading={requestMutation.isPending}>
          {t('auth.forgot.sendCode')}
        </Button>

        <Link to={ROUTES.login} className="text-center text-sm text-primary hover:underline">
          {t('auth.forgot.backToSignIn')}
        </Link>
      </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...confirmForm}>
    <form
      className="flex flex-col gap-4"
      onSubmit={confirmForm.handleSubmit((v) => {
        setSubmitError(null);
        confirmMutation.mutate(v);
      })}
      noValidate
    >
      <p className="text-sm opacity-70">
        {t('auth.forgot.step2Hint', { identity })}
      </p>
      <FormField label={t('common.code')} required error={confirmForm.formState.errors.code?.message}>
        {(p) => (
          <Input
            {...p}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            className="text-center text-lg tracking-[0.4em]"
            {...confirmForm.register('code')}
          />
        )}
      </FormField>

      <FormField
        label={t('auth.forgot.newPassword')}
        required
        error={confirmForm.formState.errors.newPassword?.message}
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            {...confirmForm.register('newPassword')}
          />
        )}
      </FormField>

      <FormField
        label={t('auth.forgot.confirmPassword')}
        required
        error={confirmForm.formState.errors.confirmPassword?.message}
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            {...confirmForm.register('confirmPassword')}
          />
        )}
      </FormField>

      {submitError ? (
        <Alert type="error" showIcon message={submitError} />
      ) : null}

      <Button type="submit" loading={confirmMutation.isPending}>
        {t('auth.forgot.setNewPassword')}
      </Button>

      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          setSubmitError(null);
          setStep('request');
        }}
      >
        {t('auth.forgot.useDifferentEmail')}
      </Button>
    </form>
    </FormProvider>
  );
}
